import { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ADSENSE_CLIENT, SLOT_BANNER, adsLive, ensureAdsenseScript, pushAd, nextHouseAd } from "@/lib/ads";

// Persistent top banner. Remounts on every route change (keyed by pathname in the parent),
// so a fresh ad loads on each page. Live AdSense when configured, themed placeholder otherwise.
export default function AdBanner({ height = 90 }) {
  const location = useLocation();
  const navigate = useNavigate();
  const insRef = useRef(null);
  const live = adsLive && !!SLOT_BANNER; // need both client + ad-unit slot to serve
  const compact = height < 60;

  useEffect(() => {
    if (live) { ensureAdsenseScript(); pushAd(); }
  }, [location.pathname, live]);

  // pick a fresh house ad per page load (placeholder mode)
  const house = useMemo(() => nextHouseAd(), [location.pathname]);

  return (
    <div
      data-testid="ad-banner"
      className="relative w-full rounded-[var(--radius)] border border-border/70 bg-card/60 backdrop-blur overflow-hidden"
      style={{ height }}
    >
      <span className="absolute top-0.5 right-2 z-10 text-[8px] uppercase tracking-widest text-muted-foreground/70 mono pointer-events-none">Advertisement</span>

      {live ? (
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: "block", width: "100%", height }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={SLOT_BANNER}
          data-ad-format="horizontal"
          data-full-width-responsive="true"
        />
      ) : compact ? (
        <div className="flex items-center gap-3 h-full pl-4 pr-24 sm:pr-28" style={{ height }}>
          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
          <div className="min-w-0 flex-1 flex items-baseline gap-2">
            <span className="font-head font-bold text-xs truncate">{house.title}</span>
            <span className="text-[11px] text-muted-foreground truncate hidden sm:inline">{house.body}</span>
          </div>
          <button
            data-testid="ad-banner-cta"
            onClick={() => navigate(house.to)}
            className="absolute right-16 sm:right-20 top-1/2 -translate-y-1/2 text-[11px] font-bold text-primary hover:underline whitespace-nowrap"
          >
            {house.cta} →
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4 px-4 sm:px-6 h-full" style={{ height }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-[var(--radius)] bg-primary/15 border border-primary/30 grid place-items-center shrink-0">
              <span className="font-head font-black text-primary text-sm">O</span>
            </div>
            <div className="min-w-0">
              <div className="font-head font-bold text-sm truncate">{house.title}</div>
              <div className="text-xs text-muted-foreground truncate">{house.body}</div>
            </div>
          </div>
          <button
            data-testid="ad-banner-cta"
            onClick={() => navigate(house.to)}
            className="btn-primary text-xs px-4 py-2 shrink-0"
          >
            {house.cta}
          </button>
        </div>
      )}
    </div>
  );
}
